import React, { useState, useRef, useEffect } from 'react';
import { supabase } from '../supabase';
import styles from './EntryLoginScreen.module.css';

const ARROW_SVG = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);
const EYE_ON = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const EYE_OFF = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);
const CHECK_CIRCLE = (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

export function EntryLoginScreen({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [shaking, setShaking] = useState(false);
  const [mode, setMode] = useState('login');         // 'login' | 'forgot' | 'reset_sent'
  const [resetMsg, setResetMsg] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailHasError, setEmailHasError] = useState(false);
  const [passwordHasError, setPasswordHasError] = useState(false);
  const emailInputRef = useRef(null);

  useEffect(() => {
    if (mode === 'login' || mode === 'forgot') {
      emailInputRef.current?.focus();
    }
  }, [mode]);

  function triggerShake() {
    setShaking(true);
    setTimeout(() => setShaking(false), 400);
  }

  function isValidEmail(s) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
  }

  async function handleSignIn(e) {
    e.preventDefault();
    setError('');
    setEmailHasError(false);
    setPasswordHasError(false);

    // Read DOM values directly to handle Chrome autofill desync
    // (autofill can populate the input without firing a synthetic React onChange,
    // leaving React state stale relative to what the user actually sees).
    const emailValue = (document.getElementById('login-email')?.value || email).trim();
    const passwordValue = document.getElementById('login-password')?.value || password;

    // Sync state back so the UI reflects what we're actually submitting
    if (emailValue !== email) setEmail(emailValue);
    if (passwordValue !== password) setPassword(passwordValue);

    if (!isValidEmail(emailValue)) {
      setEmailHasError(true);
      setError('Please enter a valid email address.');
      triggerShake();
      return;
    }
    if (!passwordValue) {
      setPasswordHasError(true);
      setError('Please enter your password.');
      triggerShake();
      return;
    }

    setLoading(true);
    const { data, error: err } = await supabase.auth.signInWithPassword({
      email: emailValue,
      password: passwordValue,
    });
    setLoading(false);

    if (err) {
      setError(err.message || 'Sign in failed. Please try again.');
      setEmailHasError(true);
      setPasswordHasError(true);
      triggerShake();
      return;
    }
    onLogin(data.user);
  }

  async function handleForgot(e) {
    e.preventDefault();
    setError('');
    setEmailHasError(false);

    const emailValue = (document.getElementById('login-email')?.value || email).trim();
    if (emailValue !== email) setEmail(emailValue);

    if (!isValidEmail(emailValue)) {
      setEmailHasError(true);
      setError('Please enter a valid email address.');
      triggerShake();
      return;
    }

    setLoading(true);
    const { error: err } = await supabase.auth.resetPasswordForEmail(emailValue, {
      redirectTo: 'https://carolina-lemke-catalog-ujup.vercel.app',
    });
    setLoading(false);

    if (err) {
      setError(err.message || 'Could not send reset link. Please try again.');
      triggerShake();
      return;
    }
    setResetMsg(email);
    setMode('reset_sent');
  }

  function backToLogin() {
    setMode('login');
    setError('');
    setResetMsg('');
    setEmailHasError(false);
    setPasswordHasError(false);
  }

  return (
    <div className={styles.loginRoot}>
      <div className={`${styles.card} ${shaking ? styles.cardShake : ''}`}>
        <div className={styles.brand}>
          <span className={styles.brandLogo} aria-label="Carolina Lemke Berlin">
            <span className={styles.brandTop}>Carolina Lemke</span>
            <span className={styles.brandBottom}>Berlin</span>
          </span>
        </div>

        <div className={styles.brandDivider} aria-hidden="true"></div>
        <div className={styles.brandLine3}>Operations Hub</div>
        <div className={styles.separator}></div>

        {mode === 'reset_sent' ? (
          <div className={styles.resetSent}>
            <div className={styles.resetSentIcon}>{CHECK_CIRCLE}</div>
            <p className={styles.resetSentMsg}>
              We sent a reset link to <span className={styles.resetSentEmail}>{resetMsg}</span>.
            </p>
            <p className={styles.resetSentHint}>Check your inbox and follow the link to reset your password.</p>
            <button type="button" className={styles.resetSentBack} onClick={backToLogin}>
              Back to sign in
            </button>
          </div>
        ) : (
          <>
            <div className={styles.welcome}>
              <h1 className={`${styles.welcomeTitle} ${mode === 'forgot' ? styles.welcomeTitleMode2 : ''}`}>
                {mode === 'login' ? 'Welcome back' : 'Reset your password'}
              </h1>
              <p className={styles.welcomeSub}>
                {mode === 'login' ? 'Sign in to your workspace' : "Enter your email and we'll send you a reset link."}
              </p>
            </div>

            <form className={styles.form} onSubmit={mode === 'login' ? handleSignIn : handleForgot}>
              <div className={`${styles.formField} ${emailHasError ? styles.formFieldError : ''}`}>
                <div className={styles.fieldHead}>
                  <label className={styles.fieldLabel} htmlFor="login-email">Email</label>
                </div>
                <div className={styles.fieldWrap}>
                  <input
                    id="login-email"
                    name="email"
                    ref={emailInputRef}
                    type="email"
                    className={styles.fieldInput}
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setEmailHasError(false); }}
                    autoComplete="email"
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              {mode === 'login' && (
                <div className={`${styles.formField} ${passwordHasError ? styles.formFieldError : ''}`}>
                  <div className={styles.fieldHead}>
                    <label className={styles.fieldLabel} htmlFor="login-password">Password</label>
                    <button
                      type="button"
                      className={styles.fieldAction}
                      onClick={() => { setMode('forgot'); setError(''); setPasswordHasError(false); }}
                    >
                      Forgot?
                    </button>
                  </div>
                  <div className={styles.fieldWrap}>
                    <input
                      id="login-password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      className={styles.fieldInput}
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); setPasswordHasError(false); }}
                      autoComplete="current-password"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      className={styles.fieldToggle}
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? EYE_OFF : EYE_ON}
                    </button>
                  </div>
                </div>
              )}

              {error && <div className={styles.fieldError} role="alert">{error}</div>}

              {mode === 'login' && (
                <div className={styles.options}>
                  <label className={styles.remember}>
                    <input type="checkbox" defaultChecked />
                    <span className={styles.rememberLabel}>Keep me signed in</span>
                  </label>
                </div>
              )}

              <button
                type="submit"
                className={`${styles.submit} ${loading ? styles.submitLoading : ''}`}
                disabled={loading}
                data-test="login-submit"
              >
                <span>{mode === 'login' ? 'Sign in' : 'Send reset link'}</span>
                <span className={styles.submitArrow}>{ARROW_SVG}</span>
              </button>
            </form>

            <div className={styles.meta}>
              {mode === 'login' ? (
                <>
                  <button type="button" className={styles.metaSso}>
                    Or <strong>use SSO</strong>
                  </button>
                  <button type="button" className={styles.metaLang}>
                    EN
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                </>
              ) : (
                <button type="button" className={styles.metaSso} onClick={backToLogin}>
                  ← <strong>Back to sign in</strong>
                </button>
              )}
            </div>
          </>
        )}
      </div>

      <div className={styles.footer}>
        © 2026 Carolina Lemke Berlin · Operations Hub v2.0 · Secure connection
      </div>
    </div>
  );
}

export default EntryLoginScreen;
